import { useCallback, useEffect, useMemo, useState } from "react";
import { ConfirmDialog } from "../components/ConfirmDialog";
import { IconButton } from "../components/IconButton";
import { IconBan, IconCopy, IconPause, IconPlay, IconRefresh, IconTrash } from "../components/icons";
import { usePageRefresh } from "../lib/page-lifecycle";
import { formatKathmanduWhen } from "../lib/nepal-time";
import { type ApiKeyRow, devApi } from "./api";

const SCOPE_OPTIONS = [
  { id: "devices:read", label: "Camera Read" },
  { id: "devices:write", label: "Camera Write" },
  { id: "streams:read", label: "Live Stream / Listen" },
  { id: "streams:control", label: "Stream Control" },
  { id: "talk:write", label: "PTT / Two-way audio" },
  { id: "positions:read", label: "GPS History" },
  { id: "alarms:read", label: "Alarms" },
  { id: "gps:live", label: "Live GPS" },
];

function scopeLabel(scope: string): string {
  return SCOPE_OPTIONS.find((s) => s.id === scope)?.label ?? scope;
}

type PendingKey = { id: string; name: string; action: "delete" | "revoke" };

export function KeysPage() {
  const [keys, setKeys] = useState<ApiKeyRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [plaintext, setPlaintext] = useState<string | null>(null);
  const [copied, setCopied] = useState<string | null>(null);
  const [name, setName] = useState("My Web App");
  const [environment, setEnvironment] = useState<"live" | "test">("live");
  const [busy, setBusy] = useState(false);
  const [pending, setPending] = useState<PendingKey | null>(null);
  const [scopes, setScopes] = useState<string[]>([
    "devices:read",
    "streams:read",
    "streams:control",
    "talk:write",
    "alarms:read",
    "gps:live",
  ]);

  const load = useCallback(async () => {
    try {
      const data = await devApi<{ keys: ApiKeyRow[] }>("/api/developers/keys");
      setKeys(data.keys);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load keys");
    }
  }, []);

  usePageRefresh(load);

  useEffect(() => {
    void load();
  }, [load]);

  const create = async () => {
    setBusy(true);
    setError(null);
    try {
      const data = await devApi<{ plaintext: string; key: ApiKeyRow }>("/api/developers/keys", {
        method: "POST",
        body: JSON.stringify({ name, environment, scopes }),
      });
      setPlaintext(data.plaintext);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Create failed");
    } finally {
      setBusy(false);
    }
  };

  const act = async (id: string, action: "revoke" | "rotate" | "disable" | "enable" | "delete") => {
    setBusy(true);
    setError(null);
    try {
      if (action === "delete") {
        await devApi(`/api/developers/keys/${id}`, { method: "DELETE" });
      } else {
        const data = await devApi<{ plaintext?: string }>(`/api/developers/keys/${id}/${action}`, { method: "POST" });
        if (data.plaintext) setPlaintext(data.plaintext);
      }
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Request failed");
    } finally {
      setBusy(false);
    }
  };

  const copy = async (value: string, label: string) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(label);
      window.setTimeout(() => setCopied(null), 1600);
    } catch {
      setError("Could not copy to clipboard");
    }
  };

  const toggleScope = (scope: string) => {
    setScopes((cur) => (cur.includes(scope) ? cur.filter((s) => s !== scope) : [...cur, scope]));
  };

  const confirmMessage = useMemo(() => {
    if (!pending) return "";
    if (pending.action === "delete") return `Delete API key “${pending.name}”? This cannot be undone.`;
    return `Revoke API key “${pending.name}”? It will stop working immediately.`;
  }, [pending]);

  return (
    <div className="keys-page">
      <section className="keys-create">
        <h2>Create API key</h2>
        <div className="device-form">
          <label>
            Name / label
            <input value={name} onChange={(e) => setName(e.target.value)} />
          </label>
          <label>
            Environment
            <select value={environment} onChange={(e) => setEnvironment(e.target.value as "live" | "test")}>
              <option value="live">live (dc_live_)</option>
              <option value="test">test (dc_test_)</option>
            </select>
          </label>
          <fieldset className="devportal-scopes">
            <legend>Permissions / scopes</legend>
            {SCOPE_OPTIONS.map((scope) => (
              <label key={scope.id} className="checkbox">
                <input type="checkbox" checked={scopes.includes(scope.id)} onChange={() => toggleScope(scope.id)} />
                {scope.label} <code>{scope.id}</code>
              </label>
            ))}
          </fieldset>
          <button type="button" className="primary" disabled={busy} onClick={() => void create()}>
            Generate
          </button>
        </div>
        {plaintext && (
          <div className="devportal-secret">
            <p>
              <strong>API key created</strong> — copy it now. The full secret is shown only once.
            </p>
            <code>{plaintext}</code>
            <IconButton label="Copy API key" onClick={() => void copy(plaintext, "key")}>
              <IconCopy />
              <span className="icon-btn-label">{copied === "key" ? "Copied" : "Copy"}</span>
            </IconButton>
          </div>
        )}
        {error && <p className="form-error">{error}</p>}
        {copied && <p className="form-success">Copied {copied}.</p>}
      </section>

      <section className="keys-list-wrap">
        <h2>Keys</h2>
        <ul className="keys-list">
          {keys.map((key) => (
            <li key={key.id} className={`key-card is-${key.status}`}>
              <div className="key-card-main">
                <div className="key-card-title">
                  <strong>{key.name}</strong>
                  <span className={`key-status is-${key.status}`}>{key.status}</span>
                </div>
                <p>
                  {key.environment} · {key.masked}
                </p>
                <p>
                  Created {key.created_at ? formatKathmanduWhen(key.created_at) : "—"} · Last used{" "}
                  {key.last_used_at ? formatKathmanduWhen(key.last_used_at) : "never"}
                </p>
                <p>Permissions: {key.scopes.map(scopeLabel).join(", ") || "none"}</p>
              </div>
              <div className="row-actions key-actions">
                {key.status !== "revoked" && (
                  <>
                    <IconButton label="Copy prefix" onClick={() => void copy(key.prefix, "prefix")}>
                      <IconCopy />
                    </IconButton>
                    {key.status === "active" ? (
                      <IconButton label="Disable" disabled={busy} onClick={() => void act(key.id, "disable")}>
                        <IconPause />
                      </IconButton>
                    ) : (
                      <IconButton label="Enable" disabled={busy} onClick={() => void act(key.id, "enable")}>
                        <IconPlay />
                      </IconButton>
                    )}
                    <IconButton label="Regenerate" disabled={busy} onClick={() => void act(key.id, "rotate")}>
                      <IconRefresh />
                    </IconButton>
                    <IconButton
                      label="Revoke"
                      danger
                      disabled={busy}
                      onClick={() => setPending({ id: key.id, name: key.name, action: "revoke" })}
                    >
                      <IconBan />
                    </IconButton>
                  </>
                )}
                <IconButton
                  label="Delete"
                  danger
                  disabled={busy}
                  onClick={() => setPending({ id: key.id, name: key.name, action: "delete" })}
                >
                  <IconTrash />
                </IconButton>
              </div>
            </li>
          ))}
        </ul>
        {keys.length === 0 && <p className="empty-grid">No keys yet.</p>}
      </section>

      <ConfirmDialog
        open={Boolean(pending)}
        danger
        title={pending?.action === "revoke" ? "Revoke API key?" : "Delete API key?"}
        message={confirmMessage}
        confirmLabel={pending?.action === "revoke" ? "Revoke" : "Delete"}
        busy={busy}
        onCancel={() => setPending(null)}
        onConfirm={() => {
          if (!pending) return;
          const next = pending;
          setPending(null);
          void act(next.id, next.action);
        }}
      />
    </div>
  );
}
